import cx from 'classnames';
import every from 'lodash/every';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import React, { PureComponent } from 'react';
import { useTranslation } from 'react-i18next';

import FieldContainer from '../components/FieldContainer';
import StringSummary from '../components/StringSummary';
import { evalCondition } from '@appcraft/common-utils';
import { isFieldLocked, replaceValues } from '../components/utils';
import { setIn } from '../utils/objectUtils';

import CdnImage from '../components/CdnImage';
import InputBlocker from '../components/InputBlocker';
import { filterOptions, filterOptionsByCategory, getOptionCategories } from '../utils/fieldUtils';
import { formatOptionDate } from '../utils/dateUtils';
import { getEffectiveMinCount } from '../utils/radioUtils';
import { ReactComponent as AngleDownIcon } from './../assets/angle-down.svg';
import './radio-field.scss';
import Quota from '../components/Quota';

export const safeParseJson = (value) => {
  if (value[0] === '{') {
    return JSON.parse(value);
  }
  return [value];
};

export const RadioOptionDate = ({ startDate, endDate }) => {
  const formattedDate = formatOptionDate(startDate, endDate);
  if (!formattedDate) return null;

  return (
    <span className="af-radio-date start-date" title={formattedDate}>
      <span className="af-radio-date__text">{formattedDate}</span>
    </span>
  );
};

export const RadioOptionContent = ({ option, strings, quotaMode }) => {
  const { label, image, description, quota, remainingPlaces, labelIfFull, isFull, startDate, endDate } = option;

  return (
    <>
      {image && (
        <div className="af-radio-image">
          <CdnImage maxWidth={120} maxHeight={120} src={image} srcSet />
        </div>
      )}
      <span>
        {(isFull && labelIfFull) || label}
        {startDate && <RadioOptionDate startDate={startDate} endDate={endDate} />}
        {description && <div className="description">{description}</div>}
        {typeof quota === 'number' && <Quota roomLeft={remainingPlaces} quota={quota} strings={strings} quotaMode={quotaMode} />}
      </span>
    </>
  );
};

export const RadioOption = ({ option, type = 'radio', name, checked, required, isLocked, strings, quotaMode, onChange }) => {
  const { value: radioValue, disabled: optionDisabled } = option;

  return (
    <label className={cx('af-radio-label', checked && 'is-active', optionDisabled && 'is-disabled')}>
      <input
        className={type === 'checkbox' ? 'af-checkbox-field' : 'af-radio-field'}
        type={type}
        name={name}
        value={radioValue}
        required={required}
        checked={checked}
        disabled={optionDisabled}
        readOnly={optionDisabled || isLocked}
        onChange={optionDisabled || isLocked ? undefined : () => onChange(radioValue)}
      />
      <RadioOptionContent option={option} strings={strings} quotaMode={quotaMode} />
    </label>
  );
};

// Dropdown displayed above the radios, to only show the options of a given category
export const CategoryFilter = ({ categories, value, onChange, disabled }) => {
  const { t } = useTranslation();
  return (
    <div className="af-radios-filter">
      <select className="af-radios-filter__select" value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('radio.all-categories')}</option>
        {categories.map((category) => (
          <option key={category} value={category}>
            {category}
          </option>
        ))}
      </select>
      <div className="af-radios-filter__arrow" aria-hidden="true">
        <AngleDownIcon />
      </div>
    </div>
  );
};

// Field strings can override the translated message, with a {minCount} placeholder
const MinCountError = ({ name, minCount, strings }) => {
  const { t } = useTranslation();
  const customMessage = get(strings, 'min-count-not-reached');
  return (
    <div className="af-message af-message--error">
      {customMessage ? replaceValues(customMessage, { minCount }) : t('radio.min-count-not-reached', { count: Number(minCount) })}
      <InputBlocker name={name} />
    </div>
  );
};

function orderTagValues(values, options) {
  return orderBy(values, (v) => findIndex(options, (opt) => opt.value === v));
}

class MultiRadioField extends PureComponent {
  state = { category: '' };

  handleCategoryChange = (category) => this.setState({ category });

  getValues = () => {
    const { value } = this.props;
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return safeParseJson(value);
    }
    console.warn('Unknown value type', typeof value, value);
    return [];
  };

  handleChange = (value) => {
    const { name, onChange, maxCount } = this.props;

    let values = this.getValues();
    if (values.indexOf(value) !== -1) {
      // Remove
      values = values.filter((v) => v !== value);
    } else {
      // Add
      values = [...values, value];
    }
    if (maxCount && values.length > maxCount) {
      return; // Block editing
    }

    // Order values in the same order as options
    values = orderTagValues(values, this.props.options);

    onChange(name, values.length === 0 ? [] : values);
  };

  render() {
    const {
      name,
      options,
      vertical,
      required = false,
      disabled,
      locked,
      strings,
      mode,
      summary,
      label,
      value,
      quotaMode,
      categoryField,
    } = this.props;
    const { category } = this.state;
    const values = this.getValues();
    const effectiveMinCount = getEffectiveMinCount(this.props);
    const isMinCountNotReached = values.length > 0 && values.length < effectiveMinCount;
    const isLocked = isFieldLocked(name, locked);
    const initialUserValue = get(window.__DATA__?.data, name);

    if (mode === 'summary') {
      const optionsByValue = keyBy(options, 'value');
      const labelValue = (value || []).map((v) => optionsByValue[v]?.label ?? v).join(', ');
      return <StringSummary label={label} value={labelValue} summary={summary} />;
    }

    const allOptions = options.map((option) => {
      const { value: radioValue, quota, usersCount } = option;
      const remainingPlaces = typeof quota === 'number' ? Math.max(0, quota - (usersCount || 0)) : undefined;
      const isInitiallySelected = Array.isArray(initialUserValue) ? initialUserValue.includes(radioValue) : initialUserValue === radioValue;
      const isFull = remainingPlaces === 0 && !isInitiallySelected;
      const optionDisabled = disabled || isFull;
      return { ...option, remainingPlaces, isFull, disabled: optionDisabled };
    });

    const categories = getOptionCategories(allOptions, categoryField);
    const optionsToShow = filterOptionsByCategory(allOptions, categoryField, category);

    return (
      <FieldContainer {...this.props} vertical={vertical} as="div">
        {categories.length > 0 && (
          <CategoryFilter name={name} categories={categories} value={category} disabled={disabled} onChange={this.handleCategoryChange} />
        )}
        <div className="af-radios">
          {/* Used to block submit */}
          <input
            name={name}
            required={required}
            value={values.length ? values.join(',') : ''}
            style={{ position: 'absolute', opacity: 0, width: '100%', height: 0 }}
          />
          {optionsToShow.map((option, idx) => {
            const checked = values.indexOf(option.value) !== -1;
            return (
              <RadioOption
                key={idx}
                type="checkbox"
                option={option}
                checked={checked}
                isLocked={isLocked}
                strings={strings}
                quotaMode={quotaMode}
                onChange={this.handleChange}
              />
            );
          })}
        </div>
        {!disabled && !isLocked && isMinCountNotReached && <MinCountError name={name} minCount={effectiveMinCount} strings={strings} />}
      </FieldContainer>
    );
  }
}

export default class RadioField extends PureComponent {
  state = { category: '' };

  handleCategoryChange = (category) => this.setState({ category });

  handleChange(name, value) {
    const { onChange, confirmationPrompt, data, inputMode } = this.props;
    if (confirmationPrompt?.message) {
      const { message, condition } = confirmationPrompt;

      const finalValue = inputMode === 'number' ? +value : value;
      const newData = setIn(data, name, finalValue);

      // Simulate new data
      if (!condition || evalCondition(condition, newData)) {
        if (!window.confirm(message)) {
          return;
        }
      }
    }
    onChange(name, value);
  }

  render() {
    const {
      data,
      mode,
      summary,
      label,
      name,
      options,
      value,
      vertical,
      required = false,
      disabled,
      locked,
      allowMultiple,
      strings,
      quotaMode,
      categoryField,
    } = this.props;
    const { category } = this.state;
    const visibleOptions = filterOptions(data, options);
    if (allowMultiple) return <MultiRadioField {...this.props} options={visibleOptions} />;
    const isLocked = isFieldLocked(name, locked);

    const initialUserValue = get(window.__DATA__?.data, name);

    if (mode === 'summary') {
      const labelValue = options.find((o) => o.value === value)?.label;
      return <StringSummary label={label} value={labelValue} summary={summary} />;
    }

    const allOptions = visibleOptions.map((option) => {
      const { value: radioValue, quota, usersCount } = option;
      const remainingPlaces = typeof quota === 'number' ? Math.max(0, quota - (usersCount || 0)) : undefined;
      const isFull = remainingPlaces <= 0 && initialUserValue !== radioValue;
      const optionDisabled = disabled || isFull;
      return { ...option, remainingPlaces, isFull, disabled: optionDisabled };
    });

    // If everything is full, the browser will disable the "required" prop, we'll need to lock it
    // Computed on all the options, not only the ones matching the selected category
    const isFieldFull = every(allOptions, 'isFull');
    const selectedOption = allOptions.find((opt) => opt.value === value);

    const categories = getOptionCategories(allOptions, categoryField);
    const optionsToShow = filterOptionsByCategory(allOptions, categoryField, category);
    // The selected option can be hidden by the category filter : keep a checked input around, otherwise the browser would block the submit
    // == to allow matching string vs number
    const isValueVisible = (opts) => opts.some((opt) => opt.value == value);
    const isSelectionHidden = value !== undefined && value !== null && value !== '' && isValueVisible(allOptions) && !isValueVisible(optionsToShow);

    return (
      <FieldContainer {...this.props} vertical={vertical} as="div">
        {!disabled && required && isFieldFull && (!value || selectedOption?.isFull) && <InputBlocker name={name} />}
        {categories.length > 0 && (
          <CategoryFilter name={name} categories={categories} value={category} disabled={disabled} onChange={this.handleCategoryChange} />
        )}
        <div className="af-radios">
          {isSelectionHidden && <input type="radio" name={name} value={value} checked readOnly style={{ display: 'none' }} />}
          {optionsToShow.map((option, idx) => {
            const checked = value == option.value;
            return (
              <RadioOption
                key={idx}
                type="radio"
                name={name}
                option={option}
                checked={checked}
                required={required}
                isLocked={isLocked}
                strings={strings}
                quotaMode={quotaMode}
                onChange={(radioVal) => this.handleChange(name, radioVal)}
              />
            );
          })}
        </div>
      </FieldContainer>
    );
  }
}
